import { t } from '@lingui/macro'
import { useCallback } from 'react'
import { toast } from 'sonner'

interface UseThreadCancelProps {
    client: any
    threadId: string | null | undefined
    currentRunId: string | null
}

export function useThreadCancel({ client, threadId, currentRunId }: UseThreadCancelProps) {
    const handleCancel = useCallback(async () => {
        try {
            if (!client || !threadId) {
                throw new Error('Missing client or thread information')
            }

            // Use the captured run_id if available, otherwise fall back to fetching pending runs
            let runId = currentRunId

            if (!runId) {
                // Fallback: get the list of pending runs for this thread
                const pendingRuns = await client.runs.list(threadId, {
                    status: 'pending',
                    limit: 1
                })

                if (!pendingRuns || pendingRuns.length === 0) {
                    throw new Error('No pending runs found to cancel')
                }

                runId = pendingRuns[0].run_id
            }

            // Cancel the specific run with interrupt action
            // This will make a POST request to /threads/{threadId}/runs/{runId}/cancel?wait=false&action=interrupt
            await client.runs.cancel(threadId, runId, false, 'interrupt')

            toast.success(t`Request cancelled`, {
                description: t`The current request has been interrupted.`,
                duration: 3000
            })
        } catch (error) {
            console.error('Failed to cancel request:', error)
            toast.error(t`Failed to cancel`, {
                description: t`Unable to cancel the current request. Please try again.`,
                duration: 3000
            })
        }
    }, [client, threadId, currentRunId])

    return {
        handleCancel
    }
}
